import type {
  FilterBadgeLabel,
  FilterParams,
  FilterTab,
  KeyLabel,
  ListOrdersParams,
  Order,
  PaymentStatus,
  PhysicalOrder,
  ProcessingStatus,
} from '@/@types/Orders';
import { PhysicalOrdersService } from '@/services/physicalOrders.service';
import useCurrencyStore from '@/store/useCurrencyStore';
import i18n from '@/translations';
import { formatDateForAPI, getDefaultDate, getDefaultOrdersDateRange, isValidDate } from '@/utils/dateHelpers';
import { PAYMENT_STATUS, PROCESSING_STATUS } from '@/utils/orders';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { differenceInDays, format, isSameDay, isToday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsIsoDateTime,
  parseAsString,
  useQueryState,
} from 'nuqs';
import { useCallback, useMemo } from 'react';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';

// Re-exporta tipos para uso em componentes
export type { FilterBadgeLabel, FilterParams, FilterTab, KeyLabel, Order, PaymentStatus, ProcessingStatus };
export type OrderStatus = ProcessingStatus;

// Re-exporta constantes para uso em componentes (ex: FilterOrders)
export { PAYMENT_STATUS, PROCESSING_STATUS };

// Configuração padrão para queries
const QUERY_CONFIG = {
  refetchOnWindowFocus: false,
  staleTime: 1000 * 60 * 2, // 2 minutos
} as const;

const getLocale = () => (i18n.language === 'pt' ? ptBR : undefined);

const extractFilterUuid = (value: string): string =>
  value.includes(':::') ? value.split(':::')[1] : value;

const mapFilterUuids = (values?: string[] | null): string[] | undefined =>
  values?.length ? values.map(extractFilterUuid) : undefined;

const defaultOrdersDateRange = getDefaultOrdersDateRange();

/**
 * Converte PhysicalOrder da API para Order usado no componente
 */
const mapApiOrderToOrder = (apiOrder: PhysicalOrder): Order => {
  const multipleTrackings = (apiOrder.delivery_trackings?.length ?? 0) > 1;
  const primaryTracking = apiOrder.delivery_tracking;

  return {
    id: apiOrder.id,
    created_at: apiOrder.created_at,
    created_at_formatted: apiOrder.created_at_formatted,
    total: apiOrder.total,
    total_formatted: apiOrder.total_formatted,
    customer: {
      id: apiOrder.customer.id,
      name: apiOrder.customer.name,
      email: apiOrder.customer.email,
    },
    payment_method: apiOrder.payment_method,
    payment_status: apiOrder.status as PaymentStatus,
    processing_status: (primaryTracking?.processing_status || 'pending_order') as OrderStatus,
    tracking: primaryTracking ? {
      carrier: primaryTracking.carrier,
      carrier_label: primaryTracking.carrier_label,
      tracking_code: primaryTracking.tracking_code,
      status: primaryTracking.processing_status,
      tracking_url: primaryTracking.tracking_url,
      provider: primaryTracking.provider,
    } : undefined,
    delivery_trackings: multipleTrackings
      ? apiOrder.delivery_trackings ?? undefined
      : primaryTracking
        ? [primaryTracking]
        : undefined,
    has_multiple_trackings: multipleTrackings,
    shipping_address: apiOrder.shipping_address,
    integration_provider: primaryTracking?.provider || 'manual',
  };
};

/**
 * Hook para gerenciar pedidos físicos com integração à API
 */
export function useOrders() {
  const { t } = useTranslation(['orders', 'common', 'sales']);
  const queryClient = useQueryClient();
  const currency = useCurrencyStore((s) => s.currency);

  // Estados da URL para paginação e busca
  const [currentPage, setCurrentPage] = useQueryState(
    'page',
    parseAsInteger.withDefault(1)
  );
  const [searchQuery, setSearchQuery] = useQueryState('q', parseAsString);
  const [activeTab, setActiveTabState] = useQueryState(
    'tab',
    parseAsString.withDefault('all')
  );

  // Estados da URL para datas
  const [fromDate, setFromDate] = useQueryState(
    'from',
    parseAsIsoDateTime.withDefault(defaultOrdersDateRange.from)
  );
  const [toDate, setToDate] = useQueryState(
    'to',
    parseAsIsoDateTime.withDefault(defaultOrdersDateRange.to)
  );

  // Estados da URL para filtros
  const [statusFilter, setStatusFilter] = useQueryState(
    'status',
    parseAsArrayOf(parseAsString)
  );
  const [processingStatusFilter, setProcessingStatusFilter] = useQueryState(
    'processing_status',
    parseAsArrayOf(parseAsString)
  );
  const [paymentMethodsFilter, setPaymentMethodsFilter] = useQueryState(
    'payment_methods',
    parseAsArrayOf(parseAsString)
  );
  const [productsFilter, setProductsFilter] = useQueryState(
    'products',
    parseAsArrayOf(parseAsString)
  );
  const [offersFilter, setOffersFilter] = useQueryState(
    'offers',
    parseAsArrayOf(parseAsString)
  );
  const [affiliatesFilter, setAffiliatesFilter] = useQueryState(
    'affiliates',
    parseAsArrayOf(parseAsString)
  );

  // Debounce da busca para evitar muitas requisições
  const [debouncedQuery] = useDebounce(searchQuery || '', 500);

  // Normaliza tab legada paid_not_processed → open
  const normalizedActiveTab = useMemo((): FilterTab => {
    const tab = activeTab || 'all';
    if (tab === 'paid_not_processed') return 'open';
    return tab as FilterTab;
  }, [activeTab]);

  // Date range para o componente
  const dateRange = useMemo(
    (): DateRange => ({
      from: fromDate,
      to: toDate,
    }),
    [fromDate, toDate]
  );

  // Parâmetros de filtro agregados
  const filterParams = useMemo((): FilterParams => {
    const params: FilterParams = {};

    if (statusFilter?.length) params.status = statusFilter;
    if (processingStatusFilter?.length) params.processing_status = processingStatusFilter;
    if (paymentMethodsFilter?.length) params.payment_methods = paymentMethodsFilter;
    if (productsFilter?.length) params.products = productsFilter;
    if (offersFilter?.length) params.offers = offersFilter;
    if (affiliatesFilter?.length) params.affiliates = affiliatesFilter;

    return params;
  }, [statusFilter, processingStatusFilter, paymentMethodsFilter, productsFilter, offersFilter, affiliatesFilter]);

  // Converte filtros para formato visual
  const filterVisualParams = useMemo(() => {
    const result: Record<string, { label?: string; value?: string }[] | string[] | string> = {};
    const SIMPLE_ARRAY_KEYS = ['payment_methods', 'status', 'processing_status'];

    Object.entries(filterParams).forEach(([key, value]) => {
      if (['from', 'to', 'q'].includes(key)) return;
      if (typeof value === 'string' && value === '') return;

      if (SIMPLE_ARRAY_KEYS.includes(key) && Array.isArray(value)) {
        const nonEmptyValues = value.filter((v) => v !== '');
        if (nonEmptyValues.length > 0) {
          result[key] = nonEmptyValues;
        }
        return;
      }

      if (Array.isArray(value)) {
        const nonEmptyValues = value.filter((v) => v !== '');
        if (nonEmptyValues.length > 0) {
          result[key] = nonEmptyValues.map((item) => {
            if (typeof item === 'string' && item.includes(':::')) {
              const [label, val] = item.split(':::');
              return { label, value: val };
            }
            return { label: item, value: item };
          });
        }
        return;
      }

      if (typeof value === 'string') {
        result[key] = value;
      }
    });

    return result;
  }, [filterParams]);

  // Calcula os filtros de processing_status baseado na tab ativa
  const tabProcessingStatusFilter = useMemo((): string[] | undefined => {
    // Se há filtros manuais, não aplicar filtro de tab
    if (processingStatusFilter?.length) return processingStatusFilter;

    switch (normalizedActiveTab) {
      case 'open':
        return ['pending_order'];
      case 'paid_processing':
        return ['processing_order'];
      default:
        return undefined;
    }
  }, [normalizedActiveTab, processingStatusFilter]);

  // Calcula os filtros de payment status baseado na tab
  const tabPaymentStatusFilter = useMemo((): string[] | undefined => {
    // Se há filtros manuais, não aplicar filtro de tab
    if (statusFilter?.length) return statusFilter;

    switch (normalizedActiveTab) {
      case 'open':
      case 'paid_processing':
        return ['paid'];
      case 'pending_payment':
        return ['awaiting_payment'];
      default:
        return undefined;
    }
  }, [normalizedActiveTab, statusFilter]);

  // Parâmetros para a API
  const apiParams = useMemo((): ListOrdersParams => {
    const startDate = isValidDate(fromDate)
      ? formatDateForAPI(fromDate)
      : formatDateForAPI(getDefaultDate());
    const endDate = isValidDate(toDate)
      ? formatDateForAPI(toDate)
      : formatDateForAPI(getDefaultDate());

    return {
      start_date: startDate,
      end_date: endDate,
      q: debouncedQuery || undefined,
      processing_status: tabProcessingStatusFilter,
      status: tabPaymentStatusFilter,
      payment_methods: paymentMethodsFilter || undefined,
      products: mapFilterUuids(productsFilter),
      offers: mapFilterUuids(offersFilter),
      affiliates: mapFilterUuids(affiliatesFilter),
      page: currentPage,
      currency,
    };
  }, [
    fromDate,
    toDate,
    debouncedQuery,
    tabProcessingStatusFilter,
    tabPaymentStatusFilter,
    paymentMethodsFilter,
    productsFilter,
    offersFilter,
    affiliatesFilter,
    currentPage,
    currency,
  ]);

  // Parâmetros para summary (sem paginação e sem filtros de aba)
  const summaryApiParams = useMemo(() => {
    const startDate = isValidDate(fromDate)
      ? formatDateForAPI(fromDate)
      : formatDateForAPI(getDefaultDate());
    const endDate = isValidDate(toDate)
      ? formatDateForAPI(toDate)
      : formatDateForAPI(getDefaultDate());

    return {
      start_date: startDate,
      end_date: endDate,
      q: debouncedQuery || undefined,
      status: statusFilter || undefined,
      processing_status: processingStatusFilter || undefined,
      payment_methods: paymentMethodsFilter || undefined,
      products: mapFilterUuids(productsFilter),
      offers: mapFilterUuids(offersFilter),
      affiliates: mapFilterUuids(affiliatesFilter),
      currency,
    };
  }, [
    fromDate,
    toDate,
    debouncedQuery,
    statusFilter,
    processingStatusFilter,
    paymentMethodsFilter,
    productsFilter,
    offersFilter,
    affiliatesFilter,
    currency,
  ]);

  const summaryQueryKey = useMemo(() => [
    'physical-orders-summary',
    summaryApiParams.start_date,
    summaryApiParams.end_date,
    summaryApiParams.currency,
    JSON.stringify({
      q: summaryApiParams.q,
      status: summaryApiParams.status,
      processing_status: summaryApiParams.processing_status,
      payment_methods: summaryApiParams.payment_methods,
      products: summaryApiParams.products,
      offers: summaryApiParams.offers,
      affiliates: summaryApiParams.affiliates,
    }),
  ], [summaryApiParams]);

  // Query key para invalidação
  const queryKey = useMemo(() => [
    'physical-orders',
    apiParams.start_date,
    apiParams.end_date,
    apiParams.page,
    JSON.stringify({
      q: apiParams.q,
      processing_status: apiParams.processing_status,
      status: apiParams.status,
      payment_methods: apiParams.payment_methods,
      products: apiParams.products,
      offers: apiParams.offers,
      affiliates: apiParams.affiliates,
    }),
  ], [apiParams]);

  const {
    data: summaryResponse,
    isLoading: isLoadingSummary,
  } = useQuery({
    queryKey: summaryQueryKey,
    queryFn: () => PhysicalOrdersService.summary(summaryApiParams),
    ...QUERY_CONFIG,
  });

  const summaryCounts = useMemo(() => ({
    open: summaryResponse?.open ?? 0,
    paidProcessing: summaryResponse?.paid_processing ?? 0,
    delivered: summaryResponse?.delivered ?? 0,
    returned: summaryResponse?.returned ?? 0,
  }), [summaryResponse]);

  // Fetch dos pedidos
  const {
    data: apiResponse,
    isLoading,
    isRefetching,
    error,
  } = useQuery({
    queryKey,
    queryFn: () => PhysicalOrdersService.list(apiParams),
    ...QUERY_CONFIG,
  });

  // Mapeia os pedidos da API para o formato do componente
  const orders = useMemo(() => {
    if (!apiResponse?.data) return [];
    return apiResponse.data.map(mapApiOrderToOrder);
  }, [apiResponse?.data]);

  // Informações de paginação
  const pagination = useMemo(() => ({
    currentPage: apiResponse?.meta?.current_page || 1,
    lastPage: apiResponse?.meta?.last_page || 1,
    total: apiResponse?.meta?.total || 0,
    perPage: apiResponse?.meta?.per_page || 15,
  }), [apiResponse?.meta]);

  // Dados paginados para TableListCustom (meta + links da API)
  const tableData = useMemo(() => ({
    data: orders,
    meta: apiResponse?.meta ?? {
      current_page: currentPage,
      last_page: 1,
      total: 0,
      per_page: 15,
      from: null,
      to: null,
    },
    links: apiResponse?.links ?? {
      first: null,
      last: null,
      prev: null,
      next: null,
    },
  }), [orders, apiResponse?.meta, apiResponse?.links, currentPage]);

  // Filtros para exportação (mesmos da listagem, sem paginação)
  const exportFilterParams = useMemo(() => {
    const params: Record<string, string[]> = {};

    if (apiParams.status?.length) params.status = apiParams.status;
    if (apiParams.processing_status?.length) params.processing_status = apiParams.processing_status;
    if (apiParams.payment_methods?.length) params.payment_methods = apiParams.payment_methods;
    if (apiParams.products?.length) params.products = apiParams.products;
    if (apiParams.offers?.length) params.offers = apiParams.offers;
    if (apiParams.affiliates?.length) params.affiliates = apiParams.affiliates;

    return params;
  }, [apiParams]);

  const exportApiParams = useMemo(() => ({
    startDate: apiParams.start_date,
    endDate: apiParams.end_date,
    q: apiParams.q,
    filterParams: exportFilterParams,
    currency,
  }), [apiParams, exportFilterParams, currency]);

  // Atualiza o status de um pedido
  const updateOrderStatus = useCallback(async (orderId: string, newStatus: OrderStatus) => {
    // Optimistic update
    const previousData = queryClient.getQueryData(queryKey);
    queryClient.setQueryData(queryKey, (old: typeof apiResponse) => {
      if (!old?.data) return old;
      return {
        ...old,
        data: old.data.map((order) =>
          order.id === orderId
            ? {
              ...order,
              delivery_tracking: {
                ...order.delivery_tracking,
                processing_status: newStatus,
              } as typeof order.delivery_tracking,
            }
            : order
        ),
      };
    });

    try {
      // Chamada à API
      await PhysicalOrdersService.updateStatus(orderId.replace('#', ''), {
        processing_status: newStatus,
      });
      // Invalida o cache para refetch
      queryClient.invalidateQueries({ queryKey: ['physical-orders'] });
      queryClient.invalidateQueries({ queryKey: ['physical-orders-summary'] });
    } catch (error) {
      // Reverte em caso de erro
      queryClient.setQueryData(queryKey, previousData);
      throw error;
    }
  }, [queryClient, queryKey]);

  // Invalida cache para refetch
  const refetchOrders = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['physical-orders'] });
    queryClient.invalidateQueries({ queryKey: ['physical-orders-summary'] });
  }, [queryClient]);

  // Handler para mudança de busca
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value || null);
    setCurrentPage(1);
  }, [setSearchQuery, setCurrentPage]);

  // Handler para mudança de tab
  const handleTabChange = useCallback((tab: FilterTab) => {
    setActiveTabState(tab);
    setCurrentPage(1);
  }, [setActiveTabState, setCurrentPage]);

  // Handler para mudança de página
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, [setCurrentPage]);

  // Handler para mudança de date range
  const handleDateRangeChange = useCallback(
    (range: DateRange | undefined) => {
      setCurrentPage(1);
      if (range?.from) {
        setFromDate(range.from);
      }
      if (range?.to) {
        setToDate(range.to);
      }
    },
    [setCurrentPage, setFromDate, setToDate]
  );

  // Função para texto do período
  const getOrdersPeriodText = useCallback(
    (range: DateRange | undefined): string => {
      const locale = getLocale();
      if (!range?.from) return t('orders:title');

      if (range.to && isSameDay(range.from, range.to) && isToday(range.from)) {
        return t('common:today');
      }

      if (range.to && isSameDay(range.from, range.to)) {
        return format(range.from, 'dd/MM/yyyy', { locale });
      }

      if (range.to) {
        const days = differenceInDays(range.to, range.from);
        if (days === 6) return t('common:last_7_days');
        if (days === 29) return t('common:last_30_days');
        if (days === 89) return t('common:last_90_days');

        return `${format(range.from, 'dd/MM', { locale })} - ${format(range.to, 'dd/MM', { locale })}`;
      }

      return t('orders:title');
    },
    [t]
  );

  // Setter para filtros (usado pelo componente de filtros)
  const setFilterParams = useCallback(
    (params: FilterParams | ((prev: FilterParams) => FilterParams)) => {
      const newParams = typeof params === 'function' ? params(filterParams) : params;

      setStatusFilter((newParams.status as string[]) || null);
      setProcessingStatusFilter((newParams.processing_status as string[]) || null);
      setPaymentMethodsFilter((newParams.payment_methods as string[]) || null);
      setProductsFilter((newParams.products as string[]) || null);
      setOffersFilter((newParams.offers as string[]) || null);
      setAffiliatesFilter((newParams.affiliates as string[]) || null);
      setCurrentPage(1);
    },
    [
      filterParams,
      setStatusFilter,
      setProcessingStatusFilter,
      setPaymentMethodsFilter,
      setProductsFilter,
      setOffersFilter,
      setAffiliatesFilter,
      setCurrentPage,
    ]
  );

  // Remove um item de filtro específico
  const removeFilterItem = useCallback(
    (key: string, index: number) => {
      const filterSetters: Record<string, (v: string[] | null) => void> = {
        status: setStatusFilter,
        processing_status: setProcessingStatusFilter,
        payment_methods: setPaymentMethodsFilter,
        products: setProductsFilter,
        offers: setOffersFilter,
        affiliates: setAffiliatesFilter,
      };

      const setter = filterSetters[key];
      if (setter) {
        const currentValues = filterParams[key] as string[];
        const newValues = currentValues?.filter((_, i) => i !== index);
        setter(newValues?.length ? newValues : null);
      }
    },
    [
      filterParams,
      setStatusFilter,
      setProcessingStatusFilter,
      setPaymentMethodsFilter,
      setProductsFilter,
      setOffersFilter,
      setAffiliatesFilter,
    ]
  );

  // Remove um filtro inteiro
  const removeFilter = useCallback(
    (key: string) => {
      const filterSetters: Record<string, (v: string[] | null) => void> = {
        status: setStatusFilter,
        processing_status: setProcessingStatusFilter,
        payment_methods: setPaymentMethodsFilter,
        products: setProductsFilter,
        offers: setOffersFilter,
        affiliates: setAffiliatesFilter,
      };

      const setter = filterSetters[key];
      if (setter) {
        setter(null);
      }
    },
    [
      setStatusFilter,
      setProcessingStatusFilter,
      setPaymentMethodsFilter,
      setProductsFilter,
      setOffersFilter,
      setAffiliatesFilter,
    ]
  );

  // Limpa todos os filtros
  const clearAllFilters = useCallback(() => {
    setStatusFilter(null);
    setProcessingStatusFilter(null);
    setPaymentMethodsFilter(null);
    setProductsFilter(null);
    setOffersFilter(null);
    setAffiliatesFilter(null);
    setSearchQuery(null);
    setActiveTabState('all');
    setCurrentPage(1);
    handleDateRangeChange(getDefaultOrdersDateRange());
  }, [
    setStatusFilter,
    setProcessingStatusFilter,
    setPaymentMethodsFilter,
    setProductsFilter,
    setOffersFilter,
    setAffiliatesFilter,
    setSearchQuery,
    setActiveTabState,
    setCurrentPage,
    handleDateRangeChange,
  ]);

  // Labels para badges de filtros
  const filterBadgeLabel: FilterBadgeLabel = useMemo(
    () => ({
      // Payment status
      paid: t('common:paid'),
      awaiting_payment: t('common:awaiting_payment'),
      not_authorized: t('common:not_authorized'),
      refunded: t('common:refunded'),
      // Processing status
      pending_order: t('orders:status.pending_order'),
      processing_order: t('orders:status.processing_order'),
      shipped: t('orders:status.shipped'),
      in_transit: t('orders:status.in_transit'),
      out_for_delivery: t('orders:status.out_for_delivery'),
      delivered: t('orders:status.delivered'),
      delivery_failed: t('orders:status.delivery_failed'),
      cancelled_order: t('orders:status.cancelled_order'),
      returned: t('orders:status.returned'),
      // Payment methods
      pix: t('common:pix'),
      credit_card: t('common:credit_card'),
      bank_slip: t('common:bank_slip'),
      wallet_picpay: t('common:wallet_picpay'),
      wallet_google_pay: t('common:wallet_google_pay'),
      wallet_apple_pay: t('common:wallet_apple_pay'),
      venmo: t('common:venmo'),
      paze: t('common:paze'),
    }),
    [t]
  );

  // Labels para chaves de filtros
  const keyLabel: KeyLabel = useMemo(
    () => ({
      status: t('orders:table.payment_status'),
      processing_status: t('orders:table.processing_status'),
      payment_methods: t('orders:table.payment_method'),
      products: t('common:products'),
      offers: t('common:offers'),
      affiliates: t('sales:affiliate'),
    }),
    [t]
  );

  // Verifica se há filtros ativos além de datas
  const hasActiveFilters = useMemo(() => {
    return Object.keys(filterParams).length > 0 || (searchQuery && searchQuery.length > 0);
  }, [filterParams, searchQuery]);

  return {
    // Dados
    orders,
    tableData,
    summaryCounts,

    // Estados de carregamento
    isLoading,
    isLoadingSummary,
    isRefetching,
    error,

    // Filtros de tab
    activeTab: normalizedActiveTab,
    setActiveTab: handleTabChange,

    // Exportação
    exportApiParams,

    // Busca
    searchQuery: searchQuery || '',
    handleSearchChange,

    // Datas
    dateRange,
    handleDateRangeChange,
    getOrdersPeriodText,

    // Paginação
    currentPage: pagination.currentPage,
    lastPage: pagination.lastPage,
    total: pagination.total,
    setCurrentPage: handlePageChange,

    // Filtros avançados
    filterParams,
    setFilterParams,
    filterVisualParams,
    hasActiveFilters,
    removeFilterItem,
    removeFilter,
    clearAllFilters,
    filterBadgeLabel,
    keyLabel,

    // Ações
    updateOrderStatus,
    refetchOrders,
  };
}
